import {
	META_ECOMMERCE_TYPE,
	ECOMMERCE_TYPE_ZYRO,
	PAGE_TYPE_ECOMMERCE_PRODUCT,
} from '@zyro-inc/site-modules/constants';
import { getStoreId } from '@zyro-inc/site-modules/utils/getters/getStoreId';
import { getCartData } from '@zyro-inc/site-modules/utils/ecommerce/cartData';
import {
	getStoreProducts,
	getVariantsQuantity,
	getCategories,
	getStoreShippingOptions,
	getStoreRegions,
} from '@zyro-inc/site-modules/api/StoreApi';
import {
	MAX_PRODUCTS_IN_CART,
	SHOPPING_CART_STORAGE_KEY,
	SHOPPING_CART_TTL,
} from '@zyro-inc/site-modules/constants/ecommerce';
import {
	EcommerceProductType,
	EcommerceCollection,
	EcommerceProduct,
	EcommerceProductCustomField,
	EcommerceProductVariantQuantity,
} from '@zyro-inc/site-modules/types';

import {
	ref,
	computed,
} from 'vue';

import type {
	SitePage,
	EcommerceRegion,
	EcommerceShippingOption,
} from '@hostinger/builder-schema-validator';

import { useSiteGlobal } from '@zyro-inc/site-modules/use/useSiteGlobal';
import {
	getIsSameCustomField,
	getIsFreeProductTypeFree,
} from '@zyro-inc/site-modules/utils/ecommerce/productUtils';

type EcommerceRegionShippingOptions = { name: string, shippingOptions: EcommerceShippingOption[]}[];

const products = ref<EcommerceProduct[]>([]);
const regionShippingOptions = ref<EcommerceRegionShippingOptions>([]);
const categories = ref<EcommerceCollection[]>([]);
const shoppingCartItems = ref<any[]>([]);
const variantsQuantity = ref<EcommerceProductVariantQuantity[]>([]);
const selectedBookingProduct = ref<EcommerceProduct | null>(null);

const isShoppingCartOpen = ref(false);
const isCheckoutLoading = ref(false);
const isLoadingValues = ref<Record<string, boolean>>({});
const isLoadedValues = ref<Record<string, boolean>>({});
const isCategoriesLoaded = ref(false);
const isProductPageLoaded = ref(false);

export const getExistingProductWithCustomField = (
	quantifiedProducts: Array<{product: EcommerceProduct[], quantity: number}>,
	productToMatch: EcommerceProduct,
) => quantifiedProducts.find((searchedProduct: any) => {
	const isSameProduct = searchedProduct.product.variants[0].id === productToMatch.variants[0].id;

	if (searchedProduct.product.custom_fields?.length) {
		const isSameCustomField = searchedProduct.product.custom_fields.some((field: EcommerceProductCustomField) => (
			productToMatch.custom_fields?.some((customField: EcommerceProductCustomField) => (
				getIsSameCustomField(field, customField)
			))));

		return isSameProduct && isSameCustomField;
	}

	return isSameProduct;
});

export const useEcommerceGlobal = ({ blockId }: { blockId?: string } = {
	blockId: 'global',
}) => {
	const {
		meta,
		pages,
	} = useSiteGlobal();

	const isStoreTypeZyro = computed(() => meta.value[META_ECOMMERCE_TYPE] === ECOMMERCE_TYPE_ZYRO);
	const isEcommerceStoreCreated = computed(() => !!meta.value?.ecommerceStoreId);
	const isLoading = computed(() => isLoadingValues.value[blockId || ''] ?? false);
	const isLoaded = computed(() => isLoadedValues.value[blockId || ''] ?? false);

	const quantifiedCartItemsList = computed(() => shoppingCartItems.value.reduce((quantifiedProducts, product) => {
		const existingProductWithCustomField = getExistingProductWithCustomField(quantifiedProducts, product);

		if (existingProductWithCustomField) {
			return quantifiedProducts.map((productToUpdate: any) => {
				const isSameProduct = productToUpdate.product.variants[0].id === product.variants[0].id;
				const isSameCustomField = productToUpdate.product.custom_fields?.length
					? productToUpdate.product.custom_fields?.some((field: EcommerceProductCustomField) => (
						product.custom_fields?.some((customField: EcommerceProductCustomField) => (
							getIsSameCustomField(field, customField)
						))
					))
					: true;

				if (isSameProduct && isSameCustomField) {
					return {
						...productToUpdate,
						quantity: productToUpdate.quantity + 1,
					};
				}

				return productToUpdate;
			});
		}

		return [
			...quantifiedProducts,
			{
				product,
				quantity: 1,
			},
		];
	}, []));
	const shoppingCartItemCount = computed(() => {
		const freeProductIds: string[] = shoppingCartItems.value
			.filter((product) => getIsFreeProductTypeFree(product))
			.map((product) => product.id);
		const uniqueFreeProductIds = freeProductIds.filter((value, index, self) => self.indexOf(value) === index);

		const productCountWithoutFreeProducts = shoppingCartItems.value.reduce((count, item) => {
			if (uniqueFreeProductIds.includes(item.id)) {
				return count;
			}

			return count + 1;
		}, 0);

		return productCountWithoutFreeProducts + uniqueFreeProductIds.length;
	});

	const canAddToCart = (productId: string | number, productVariantId?: string) => {
		if (!productVariantId || shoppingCartItems.value.length >= MAX_PRODUCTS_IN_CART) {
			return false;
		}

		const product = products.value.find((item) => item.id === productId);
		const variant = product?.variants.find((item) => item.id === productVariantId);

		if (!product || !variant) {
			return false;
		}

		if (variant.manage_inventory) {
			const cartProduct = quantifiedCartItemsList.value
				.find((productItem: any) => productItem.product.id === productId
          && productItem.product.variants.some((variantItem: any) => variantItem.id === variant.id));
			const quantity = cartProduct?.quantity || 0;
			const availableQuantity = variantsQuantity.value.find((item) => item.id === productVariantId)?.inventory_quantity as number;

			return quantity < availableQuantity;
		}

		return true;
	};

	const productPages = computed<SitePage[]>(
		() => Object.values(pages.value).filter((page: any) => page.type === PAGE_TYPE_ECOMMERCE_PRODUCT) as SitePage[],
	);

	const setIsLoading = (payload: boolean) => {
		if (blockId) {
			isLoadingValues.value[blockId] = payload;
		}
	};

	const setIsLoaded = (payload: boolean) => {
		if (blockId) {
			isLoadedValues.value[blockId] = payload;
		}
	};

	const setIsCategoriesLoaded = (payload: boolean) => {
		isCategoriesLoaded.value = payload;
	};

	const setIsCheckoutLoading = (payload: boolean) => {
		isCheckoutLoading.value = payload;
	};

	const setRegionShippingOptions = (payload: EcommerceRegionShippingOptions) => {
		regionShippingOptions.value = payload;
	};

	const setStoreProducts = (payload: EcommerceProduct[]) => {
		const mergedProducts = payload.reduce((accumulator, product) => {
			const exists = accumulator.some((item) => item.id === product.id);

			return [
				...accumulator,
				...(exists ? [] : [product]),
			];
		}, [...products.value]);

		products.value = mergedProducts;
	};

	const setCategories = (payload: any) => {
		categories.value = payload;
	};

	const setShoppingCartOpen = (payload: boolean) => {
		isShoppingCartOpen.value = payload;
	};

	const setShoppingCartItems = (payload: any) => {
		shoppingCartItems.value = payload || [];

		const storageValue = {
			payload,
			expiry: Date.now() + SHOPPING_CART_TTL,
		};

		window.localStorage.setItem(SHOPPING_CART_STORAGE_KEY, JSON.stringify(storageValue));
	};

	const setSelectedBookingProduct = (payload: any) => {
		selectedBookingProduct.value = payload;
	};

	const setVariantsQuantity = (payload: EcommerceProductVariantQuantity[]) => {
		const mergedVariants = payload.reduce((accumulator, variant) => {
			const exists = accumulator.some((item) => item.id === variant.id);

			return [
				...accumulator,
				...(exists ? [] : [variant]),
			];
		}, [...variantsQuantity.value]);

		variantsQuantity.value = mergedVariants;
	};

	const refreshCartItems = ({ cartItems }: { cartItems: EcommerceProduct[] }) => (
		(cartItems || shoppingCartItems.value).reduce((accumulator: any, cartItem: any) => {
			const productMatch = products.value.find((productItem) => productItem.id === cartItem.id);

			const variantMatch = productMatch?.variants
				.find((variantItem) => cartItem.variants
					.some((cartVariantItem: any) => variantItem.id === cartVariantItem.id));

			const customFieldMatch = cartItem?.custom_fields?.find((customFieldItem: EcommerceProductCustomField) => cartItem.custom_fields
				.some((cartCustomFieldItem: any) => customFieldItem.id === cartCustomFieldItem.id
					&& customFieldItem.value === cartCustomFieldItem.value));

			const quantity = accumulator.reduce((counter: any, item: any) => {
				const isSameVariant = item.variants.some((variant: any) => variant.id === variantMatch?.id);
				const isSameCustomField = item.custom_fields?.every(
					(customField: any) => getIsSameCustomField(customField, customFieldMatch),
				);

				if (isSameVariant && isSameCustomField) {
					return counter + 1;
				}

				return counter;
			}, 0);

			const availableQuantity = variantsQuantity.value.find(
				((variant) => variant.id === variantMatch?.id),
			)?.inventory_quantity as number;

			const isQuantityValid = !variantMatch?.manage_inventory || quantity < availableQuantity;

			const bookingEvent = productMatch?.type.value === EcommerceProductType.BOOKING ? {
				...cartItem.variants[0].booking_event,
				time_slot: cartItem.variants[0].booking_event.time_slot,
				date: cartItem.variants[0].booking_event.date,
			} : null;

			if (productMatch && variantMatch && isQuantityValid && customFieldMatch) {
				return [
					...accumulator,
					{
						...productMatch,
						custom_fields: cartItem.custom_fields,
						variants: [
							{
								...variantMatch,
								booking_event: bookingEvent,
							},
						],
					},
				];
			}

			if (productMatch && variantMatch && isQuantityValid) {
				return [
					...accumulator,
					{
						...productMatch,
						variants: [
							{
								...variantMatch,
								booking_event: bookingEvent,
							},
						],
					},
				];
			}

			return accumulator;
		}, []));

	const updateVariantsQuantity = async (idsToFetch: string[]) => {
		const storeId = getStoreId(meta.value);

		if (!storeId) {
			return;
		}

		try {
			const quantity = await getVariantsQuantity(storeId, idsToFetch);

			setVariantsQuantity(quantity);
		} catch (error) {
			console.error(error);
		}

		const updatedCartItems = await refreshCartItems({
			cartItems: getCartData(),
		});

		setShoppingCartItems(updatedCartItems);
	};

	const fetchCategories = async (storeId: string, isCategoryListEnabled: boolean) => {
		if (!isCategoryListEnabled || categories.value.length) {
			return [];
		}

		try {
			const categoriesData = await getCategories(storeId);

			return categoriesData;
		} catch (error) {
			console.error(error);

			return [];
		}
	};

	const fetchProducts = async (productIds: string[]) => {
		const storeId = getStoreId(meta.value);

		if (!storeId) {
			return;
		}

		const cartItems = getCartData();
		const cartProductIds = cartItems.map((product: any) => product.id);
		const idsToFetch = [
			...cartProductIds,
			...productIds,
		].filter((id, index, self) => (
			!products.value.some((product) => product.id === id) && self.indexOf(id) === index
		));

		if (!idsToFetch.length) {
			if (!isLoaded.value) {
				setIsLoaded(true);
			}

			return;
		}

		// !IMPORTANT to reset isLoaded for animations
		// when not all page products are loaded, only the ones in cart, when this isLoaded=true,
		// animations will not be observed on mounted in BlockUser.vue because products load later and need to be watched into
		setIsLoaded(false);
		setIsLoading(true);

		try {
			const productData = await getStoreProducts(storeId, {
				productIds: idsToFetch,
			});
			const fetchedProductIds = productData.products.map(({ id }: {id: string}) => id);

			setStoreProducts(productData.products);

			await updateVariantsQuantity(fetchedProductIds);
		} catch (error) {
			console.error(error);
		} finally {
			setIsLoading(false);
			setIsLoaded(true);
		}
	};

	const fetchShipping = async () => {
		const storeId = getStoreId(meta.value);

		if (!storeId) {
			return;
		}

		try {
			const regions: EcommerceRegion[] = await getStoreRegions(storeId);

			const shippingRequests = regions.map(async ({
				id,
				name,
			}) => {
				const shippingOptions: EcommerceShippingOption[] = await getStoreShippingOptions(id);

				return {
					name,
					shippingOptions,
				};
			});

			const shippingOptions = await Promise.all(shippingRequests);

			setRegionShippingOptions(shippingOptions);
		} catch (error) {
			console.error(error);
		} finally {
			setIsLoading(false);
			setIsLoaded(true);
		}
	};

	return {
		products,
		regionShippingOptions,
		shoppingCartItems,
		selectedBookingProduct,
		variantsQuantity,
		isShoppingCartOpen,
		isCheckoutLoading,
		isLoading,
		isLoaded,
		isProductPageLoaded,
		isStoreTypeZyro,
		isEcommerceStoreCreated,
		quantifiedCartItemsList,
		canAddToCart,
		productPages,
		setIsLoading,
		setIsLoaded,
		setIsCheckoutLoading,
		setStoreProducts,
		setCategories,
		setShoppingCartOpen,
		setShoppingCartItems,
		setSelectedBookingProduct,
		setVariantsQuantity,
		fetchProducts,
		fetchShipping,
		updateVariantsQuantity,
		refreshCartItems,
		fetchCategories,
		categories,
		setIsCategoriesLoaded,
		isCategoriesLoaded,
		shoppingCartItemCount,
	};
};
